// @flow
import { filter, first, get, includes, size, some, sortBy } from 'lodash';
import { doctorMarkColor } from '../../elements/Calendar/doctorMark';

type Input = {
  // The clinic's professionals and rooms; the permissions say which of them the filters offer
  doctors: Array<?Object>,
  rooms: Array<Object>,
  permissions: Object,
  doctorsFilter: Array<number>,
  roomsFilter: Array<number>,
  isAllDoctors: boolean,
  isAllRooms: boolean,
  _: Function,
};

const andOthers = (list: Array<Object>) => (size(list) > 1 ? ` + ${size(list) - 1}` : '');

/**
 * Whose agenda is on the phone's screen, for the bar under its header: "Dr. Ricardo Amaral · Sala 1", with the
 * professional's agenda color (`mark`), or the wheel of colors when it's all of them or several. Null when the
 * filters have no choice to offer (one professional and one room).
 */
export const getAgendaOwner = ({
  doctors: clinicDoctors, rooms: clinicRooms, permissions, doctorsFilter, roomsFilter, isAllDoctors, isAllRooms, _,
}: Input): ?{ mark: string, text: string } => {
  // As the filters do (AgendaFilters): professionals unless the clinic is one (singleDoctor), rooms for who sees them,
  // in the filters' order. Someone who can't filter by the others (agendaDoctorFilter off) still picks between all of
  // them and their own agenda. Until every professional's data is loaded the bar waits: it can't name them yet
  const doctors = get(permissions, 'singleDoctor') ? [] : clinicDoctors;
  if (some(doctors, (doctor) => !doctor)) return null;
  const rooms = get(permissions, 'showRooms') ? sortBy(clinicRooms, 'number') : [];
  if (size(doctors) < 2 && size(rooms) < 2) return null;

  const chosenDoctors = filter(doctors, (doctor) => includes(doctorsFilter, get(doctor, 'id')));
  const chosenRooms = filter(rooms, (room) => includes(roomsFilter, room.number));
  // A filter on someone or a room not in this clinic's lists (left from another clinic): the agenda shows nothing,
  // so the bar can't name whose it is
  if (size(doctors) > 0 && !isAllDoctors && !size(chosenDoctors)) return null;
  if (size(rooms) > 1 && !isAllRooms && !size(chosenRooms)) return null;
  const firstDoctor = first(chosenDoctors);
  const oneDoctor = !isAllDoctors && size(chosenDoctors) === 1;

  let doctorsText = null;
  if (size(doctors) > 1) {
    doctorsText = isAllDoctors
      ? _('action/all_doctors', 'global')
      : `${get(firstDoctor, 'nameWithTitle') || get(firstDoctor, 'name', '')}${andOthers(chosenDoctors)}`;
  }
  const roomsText = size(rooms) > 1 && !isAllRooms && size(chosenRooms) > 0
    ? `${get(first(chosenRooms), 'name') || ''}${andOthers(chosenRooms)}`
    : null;

  // The professional's color, the wheel of colors for several, or the room's pin when the bar names only rooms
  let mark = 'all';
  if (oneDoctor) mark = doctorMarkColor(get(firstDoctor, 'color'));
  else if (!doctorsText) mark = 'room';
  return {
    mark,
    text: filter([doctorsText, roomsText]).join(' · ') || _('action/all_rooms'),
  };
};
